import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Edit,
  FileText,
  Trash2,
  User,
  Users,
  Heart,
  Link2,
  Camera,
  BookOpen,
  Archive,
  ArchiveRestore,
  AlertTriangle,
  UserPlus,
  Mic,
  FileImage,
  Plus,
  Download,
  FileJson,
  Headphones,
  Loader2,
  ChevronDown,
  Flame,
} from 'lucide-react';
import { LoadingState } from '../components/ui/LoadingState';
import { ThemeToggle } from '../components/ThemeToggle';
import { clearTreeCache } from './TreeViewEnhanced';
import { StoriesTab } from '../components/stories';
import DocumentsTab from '../components/DocumentsTab';
import { humanizeError } from '../utils/errorHandling';
import {
  CUSTOMER_IMAGE_ACCEPT,
  validateCustomerImageFile,
} from '../utils/imageUploadValidation';
import { PersonAvatarWithIndicators, PersonSelectRow } from '../components/PersonAvatarWithIndicators';
import { fetchPersonActivityMaps } from '../utils/personActivityCounts';
import { resolveAssetUrl } from '../utils/resolveAssetUrl';
import { BrandWordmark } from '../components/brand/BrandWordmark';
import { ArchiveContextNav } from '../components/archive/ArchiveContextNav';
import { PersonStudioHeader } from '../components/archive/PersonStudioHeader';
import { ArchiveHeldHereLinks } from '../components/archive/ArchiveHeldHereLinks';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { buildTreePeopleAddPersonUrl } from '../utils/archiveNavigation';
import { treeHref, personHref } from '../utils/prettyArchiveLinks';
import { getMyArchivePersonId, setMyArchivePersonId } from '../utils/myArchivePerson';
import { setSelfPersonOnServer, clearSelfPersonOnServer } from '../utils/selfPersonLink';
import { CompanionBadge } from '../components/archive/CompanionBadge';
import { MemoryMarkersSection, PetStoryAttachment } from '../components/archive/MemoryMarkersSection';
import { VideoLinkAttach } from '../components/media/VideoLinkAttach';
import { hasVideoLink } from '../utils/videoLink';
import { ArchiveSlugEditor } from '../components/archive/ArchiveSlugEditor';
import { ArchivePersonalizationSettings } from '../components/archive/ArchivePersonalizationSettings';
import { MemorialRemembranceSection, MemorialVisibilityDialog } from '../components/memorial/MemorialSettingsPanels';
import {
  PROFILE_TYPE,
  COMPANION_TYPE_OPTIONS,
  PET_SPECIES,
  getDisplayName,
  getBirthDateLabel,
  getDeathDateLabel,
  getBiographicalNotesLabel,
  isCompanionProfile,
  isGenealogicalPerson,
} from '../utils/companionProfiles';
import { ADD_CONNECTION, FAMILY_CONNECTIONS, GROW_BRANCH_COPY, LINK_EXISTING_CONNECTION_HINT } from '../utils/connectionCopy';
import { ArchiveConnectionPrivacyNotice } from '../components/archive/ArchiveConnectionPrivacyNotice';
import { WhatTheyLovedSection } from '../components/archive/WhatTheyLovedSection';
import { fetchMemoryMarkersForTree } from '../utils/memoryMarkers';
import { ProfileLinksSection } from '../components/archive/ProfileLinksSection';
import { PrivateArchiveBadge } from '../components/archive/PrivateArchiveBadge';
import { CORE_JOURNEY_UI } from '../config/testerRelease';
import { ConnectionStatusBadge, ConnectionStatusPill } from '../components/archive/ConnectionStatusBadge';
import { PersonOverviewHub } from '../components/archive/PersonOverviewHub';
import { LegacyBookPersonSetting } from '../components/legacy-book/LegacyBookPersonSetting';
import { PersonOverviewOutputsSection } from '../components/archive/PersonOverviewOutputsSection';
import { formatDisplayDate } from '../utils/dateDisplay';
import SmartDateInput from '../components/SmartDateInput';
import { PersonOverviewRichFields } from '../components/archive/PersonOverviewRichFields';
import { PersonProfilePageLayout } from '../components/archive/PersonProfilePageLayout';
import {
  PersonProfileInlineInsight,
  PersonProfileSidebarInsight,
} from '../components/archive/PersonProfileInsightPanels';
import { FirstChapterProfileCard } from '../components/archive/FirstChapterProfileCard';
import { ProfileIntentionPicker } from '../components/archive/ProfileIntentionPicker';
import { ContextualGuideTip } from '../components/guide/ContextualGuideTip';
import {
  PROFILE_INTENTION,
  getProfileIntention,
  setProfileIntention,
} from '../utils/profileIntention';
import { remindersApi } from '../utils/reminders';
import { ImportantDatesSection } from '../components/reminders/ImportantDatesSection';
import { memorialManageHref } from '../utils/memorial';
import { LEGACY_BOOK_COPY } from '../utils/humanCopy';
import { useAuth } from '../contexts/AuthContext';
import { useProfileGuidanceCollapse } from '../hooks/useProfileGuidanceCollapse';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PersonDetail = () => {
  const { user, refreshUser } = useAuth();
  const profileGuidance = useProfileGuidanceCollapse();
  const { personId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [person, setPerson] = useState(null);
  const [tree, setTree] = useState(null);
  const [relationships, setRelationships] = useState({ parents: [], children: [], partnerships: [], siblings: [] });
  const [documents, setDocuments] = useState([]);
  const [sharedDocuments, setSharedDocuments] = useState([]);
  const [storyCount, setStoryCount] = useState(0);
  const [personStories, setPersonStories] = useState([]);
  const [mentionedStories, setMentionedStories] = useState([]);
  const [allPeople, setAllPeople] = useState([]);
  /** Story/document counts for everyone in this tree (connection dropdowns + avatars) */
  const [treePersonActivity, setTreePersonActivity] = useState({
    story: {},
    doc: {},
  });
  const [loading, setLoading] = useState(true);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);
  
  // Highlight state for validation issues
  const [highlightRelationship, setHighlightRelationship] = useState(false);
  const [highlightedPersonId, setHighlightedPersonId] = useState(null);
  const [relationshipTab, setRelationshipTab] = useState('parents');
  
  const [showMemorialVisibility, setShowMemorialVisibility] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isConnectionDialogOpen, setIsConnectionDialogOpen] = useState(false);
  const [connectionStep, setConnectionStep] = useState(1);
  const [connectionType, setConnectionType] = useState('');
  const [selectedPersonId, setSelectedPersonId] = useState('');
  const [connectionDetails, setConnectionDetails] = useState({
    relationship_subtype: 'biological',
    partnership_type: 'marriage',
    start_date: '',
    start_place: '',
    notes: ''
  });
  
  const [editData, setEditData] = useState({});
  const [lifeThemesDraft, setLifeThemesDraft] = useState('');
  const [profileIntention, setProfileIntentionState] = useState(PROFILE_INTENTION.STANDARD);
  const [personReminderCount, setPersonReminderCount] = useState(0);
  
  // Archive/Delete state
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteInfo, setDeleteInfo] = useState(null);
  const [archiving, setArchiving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteAcknowledged, setDeleteAcknowledged] = useState(false);
  const [linkedAsMeInArchive, setLinkedAsMeInArchive] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [possessionCount, setPossessionCount] = useState(0);
  const [exportingJson, setExportingJson] = useState(false);

  // Handle highlight params from validation panel
  useEffect(() => {
    const highlight = searchParams.get('highlight');
    const relatedPerson = searchParams.get('related_person');
    const issueType = searchParams.get('issue_type');
    
    if (highlight === 'relationship') {
      setHighlightRelationship(true);
      if (relatedPerson) {
        setHighlightedPersonId(relatedPerson);
      }
      
      // Determine which tab to show based on issue type
      if (issueType) {
        if (issueType.includes('parent') || issueType.includes('child') || issueType.includes('generational')) {
          setRelationshipTab('parents');
        } else if (issueType.includes('spouse') || issueType.includes('partner')) {
          setRelationshipTab('partners');
        } else if (issueType.includes('sibling')) {
          setRelationshipTab('siblings');
        }
      }
      
      // Clear highlight after 5 seconds
      const timeout = setTimeout(() => {
        setHighlightRelationship(false);
        setHighlightedPersonId(null);
      }, 5000);
      
      return () => clearTimeout(timeout);
    }
  }, [searchParams]);

  // "This is me" — true if the server self_person_id matches, OR localStorage matches
  const isSelfPerson = user?.self_person_id === personId || linkedAsMeInArchive;

  useEffect(() => {
    if (!personId) return undefined;
    const sync = () => setLinkedAsMeInArchive(getMyArchivePersonId() === personId);
    // Also sync with server value on mount / user change
    if (user?.self_person_id === personId) setLinkedAsMeInArchive(true);
    sync();
    window.addEventListener('storage', sync);
    window.addEventListener('gb-my-archive-person-updated', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('gb-my-archive-person-updated', sync);
    };
  }, [personId, user?.self_person_id]);

  const fetchPersonData = useCallback(async () => {
    try {
      const personRes = await axios.get(`${API}/individuals/detail/${personId}`);
      const loaded = personRes.data;
      const pid = loaded.id;
      
      setPerson(loaded);
      setEditData(loaded);
      setRelationships({
        parents: loaded.parents || [],
        children: loaded.children || [],
        partnerships: loaded.partnerships || [],
        siblings: loaded.siblings || []
      });
      
      // Fetch documents separately
      try {
        const docsRes = await axios.get(`${API}/documents/${pid}`);
        setDocuments(docsRes.data);
      } catch {
        setDocuments([]);
      }

      try {
        const storiesRes = await axios.get(`${API}/individuals/${pid}/stories`);
        const list = Array.isArray(storiesRes.data) ? storiesRes.data : [];
        setPersonStories(list);
        setStoryCount(list.length);
      } catch {
        setPersonStories([]);
        setStoryCount(0);
      }

      try {
        const menRes = await axios.get(`${API}/individuals/${pid}/mentioned-in-stories`);
        setMentionedStories(Array.isArray(menRes.data) ? menRes.data : []);
      } catch {
        setMentionedStories([]);
      }
      
      const treeRes = await axios.get(`${API}/trees/${loaded.tree_id}`);
      setTree(treeRes.data);

      try {
        const sh = await axios.get(`${API}/trees/${loaded.tree_id}/documents/shared`);
        setSharedDocuments(Array.isArray(sh.data) ? sh.data : []);
      } catch {
        setSharedDocuments([]);
      }
      
      const peopleRes = await axios.get(`${API}/individuals/${loaded.tree_id}`);
      setAllPeople(peopleRes.data.filter((p) => p.id !== pid));
    } catch (error) {
      toast.error(humanizeError(error));
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [personId, navigate]);

  useEffect(() => {
    fetchPersonData();
  }, [fetchPersonData]);

  useEffect(() => {
    if (!person?.id) return;
    setProfileIntentionState(getProfileIntention(person));
    remindersApi
      .list()
      .then((list) => setPersonReminderCount(list.filter((r) => r.person_id === person.id).length))
      .catch(() => setPersonReminderCount(0));
  }, [person]);

  /** Keep the URL aligned with supported archive tabs (invalid ?view= no longer fights the outer Tabs). */
  useEffect(() => {
    if (loading || !personId) return;
    const v = searchParams.get('view');
    if (v === 'memorial' && person) {
      navigate(memorialManageHref(person), { replace: true });
      return;
    }
    if (v && !['overview', 'stories', 'documents', 'shared', 'family'].includes(v)) {
      navigate(`${personHref({ id: personId })}?view=overview`, { replace: true });
    }
  }, [loading, personId, person, searchParams, navigate]);

  const treePeopleKey = useMemo(() => {
    if (!person?.id) return '';
    return [person.id, ...allPeople.map((p) => p.id)].filter(Boolean).sort().join(',');
  }, [person?.id, allPeople]);

  useEffect(() => {
    if (!person?.tree_id || !treePeopleKey) return;
    const merged = [person, ...allPeople];
    const seen = new Set();
    const unique = merged.filter((p) => {
      if (!p?.id || seen.has(p.id)) return false;
      seen.add(p.id);
      return true;
    });
    let cancelled = false;
    (async () => {
      try {
        const { storyCounts, documentCounts } = await fetchPersonActivityMaps(
          BACKEND_URL,
          person.tree_id,
          unique
        );
        if (!cancelled) {
          setTreePersonActivity({ story: storyCounts, doc: documentCounts });
        }
      } catch {
        if (!cancelled) setTreePersonActivity({ story: {}, doc: {} });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- person + allPeople covered by treePeopleKey
  }, [person?.tree_id, treePeopleKey]);

  useEffect(() => {
    if (!isEditDialogOpen) return;
    const arr = editData.life_themes;
    setLifeThemesDraft(Array.isArray(arr) ? arr.join('\n') : '');
  }, [isEditDialogOpen, editData.life_themes, editData.id]);

  const voiceMemoriesCount = useMemo(
    () =>
      personStories.filter((s) => Boolean(s.audio_file_url) || s.transcript_source === 'voice').length,
    [personStories]
  );

  const featuredMemoryPreview = useMemo(() => {
    const manual = (person?.featured_memory_excerpt || '').trim();
    if (manual) {
      return { text: manual, source: 'manual', storyId: null };
    }
    const ordered = [...personStories].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    for (const s of ordered) {
      const raw = (s.polished_transcript || s.raw_transcript || '').trim().replace(/\s+/g, ' ');
      if (raw) {
        const max = 240;
        const text = raw.length > max ? `${raw.slice(0, max - 1).trimEnd()}…` : raw;
        return { text, source: 'story', storyId: s.id };
      }
    }
    return null;
  }, [person?.featured_memory_excerpt, personStories]);

  const familyConnectionCount = useMemo(() => {
    const ids = new Set();
    for (const p of relationships.parents || []) if (p?.id) ids.add(p.id);
    for (const p of relationships.children || []) if (p?.id) ids.add(p.id);
    for (const p of relationships.siblings || []) if (p?.id) ids.add(p.id);
    for (const p of relationships.partnerships || []) if (p?.id) ids.add(p.id);
    return ids.size;
  }, [relationships]);

  useEffect(() => {
    if (!person?.tree_id || !person?.id) {
      setPossessionCount(0);
      return undefined;
    }
    let cancelled = false;
    fetchMemoryMarkersForTree(person.tree_id, { personId: person.id, possessionsOnly: true })
      .then((rows) => {
        if (!cancelled) setPossessionCount(rows.length);
      })
      .catch(() => {
        if (!cancelled) setPossessionCount(0);
      });
    return () => {
      cancelled = true;
    };
  }, [person?.tree_id, person?.id]);

  const isGenealogical = isGenealogicalPerson(person);
  const editProfileType = editData.profile_type || person?.profile_type || PROFILE_TYPE.PERSON;

  /** Only genealogical people can be linked on the family tree. */
  const linkablePeople = useMemo(
    () => allPeople.filter((p) => isGenealogicalPerson(p)),
    [allPeople]
  );

  const attachedAnchorPerson = useMemo(() => {
    if (!person?.attached_to_person_id) return null;
    return (
      allPeople.find((p) => p.id === person.attached_to_person_id) ||
      null
    );
  }, [person?.attached_to_person_id, allPeople]);

  const attachedPetsForPerson = useMemo(() => {
    if (!person?.id || !isGenealogical) return [];
    return allPeople.filter(
      (p) => p.profile_type === PROFILE_TYPE.PET && p.attached_to_person_id === person.id
    );
  }, [allPeople, person?.id, isGenealogical]);

  const canonicalPersonId = person?.id;

  const handleUpdate = async () => {
    try {
      const life_themes = lifeThemesDraft
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean)
        .slice(0, 28);
      const payload = {
        given_names: editData.given_names,
        surname: editData.surname,
        suffix: editData.suffix,
        sex: editData.sex,
        profile_type: editData.profile_type,
        companion_type: editData.companion_type,
        species: editData.species,
        breed: editData.breed,
        family_connection: editData.family_connection,
        attached_to_person_id: editData.attached_to_person_id || null,
        birth_date: editData.birth_date || null,
        birth_place: editData.birth_place || null,
        death_date: editData.death_date || null,
        death_place: editData.death_place || null,
        burial_date: editData.burial_date || null,
        burial_place: editData.burial_place || null,
        biographical_notes: editData.biographical_notes || null,
        life_themes,
        featured_memory_excerpt: (editData.featured_memory_excerpt || '').trim() || null,
      };
      const targetId = person?.id || personId;
      const response = await axios.put(`${API}/individuals/${targetId}`, payload);
      setPerson((prev) => ({ ...(prev || {}), ...response.data }));
      setEditData((prev) => ({ ...(prev || {}), ...response.data, life_themes }));
      clearTreeCache(person?.tree_id);
      toast.success('Information updated!');
      setIsEditDialogOpen(false);
    } catch (error) {
      toast.error(humanizeError(error));
    }
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const check = validateCustomerImageFile(file);
    if (!check.ok) {
      toast.error(check.message);
      return;
    }

    // Validate file size (2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image too large. Maximum size is 2MB');
      return;
    }

    setUploadingPhoto(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/individuals/${personId}/photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Update person with new photo URL
      setPerson(prev => ({ ...prev, profile_image_url: response.data.url }));
      clearTreeCache(person?.tree_id);
      toast.success('Photo added!');
    } catch (error) {
      toast.error(humanizeError(error));
    } finally {
      setUploadingPhoto(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const startAddConnection = () => {
    setIsConnectionDialogOpen(true);
    setConnectionStep(1);
    setConnectionType('');
    setSelectedPersonId('');
    setConnectionDetails({
      relationship_subtype: 'biological',
      partnership_type: 'marriage',
      start_date: '',
      start_place: '',
      notes: '',
      shared_parent_id: '' // For sibling connections
    });
  };

  const handleExportPdf = async (mode = 'keepsake') => {
    const tid = person?.tree_id;
    if (!tid) return;
    setExportingPdf(true);
    try {
      const params = mode === 'review' ? { mode: 'review' } : {};
      const response = await axios.get(`${API}/trees/${tid}/export/pdf`, { responseType: 'blob', params });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const suffix = mode === 'review' ? 'Review' : 'Keepsake';
      link.download = `${tree?.name || 'Family_Archive'}_Legacy_Book_${suffix}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(
        mode === 'review'
          ? 'Your review draft PDF has been downloaded.'
          : 'Your keepsake legacy book has been downloaded.'
      );
    } catch {
      toast.error('Unable to generate PDF. Please try again.');
    } finally {
      setExportingPdf(false);
    }
  };

  const handleExportJson = async () => {
    const tid = person?.tree_id;
    if (!tid) return;
    setExportingJson(true);
    try {
      const response = await axios.get(`${API}/trees/${tid}/export/json`);
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const date = new Date().toISOString().split('T')[0].replace(/-/g, '');
      link.download = `${tree?.name || 'Family_Archive'}_backup_${date}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Your data backup has been downloaded.');
    } catch {
      toast.error('Unable to generate backup. Please try again.');
    } finally {
      setExportingJson(false);
    }
  };

  const handleOpenAudiobook = () => {
    if (!person?.tree_id) return;
    navigate(`${treeHref(tree || { id: person.tree_id })}?tab=audiobook`);
  };

  const handleAddConnection = async () => {
    if (!selectedPersonId) {
      toast.error('Please select a family connection');
      return;
    }
    const pid = person?.id;
    if (!pid) {
      toast.error('Please wait for this connection to finish loading.');
      return;
    }

    try {
      if (connectionType === 'parent') {
        await axios.post(`${API}/relationships/parent-child`, {
          tree_id: person.tree_id,
          parent_id: selectedPersonId,
          child_id: pid,
          relationship_type: connectionDetails.relationship_subtype,
          notes: connectionDetails.notes
        });
        toast.success('Parent linked on the family tree. Add more connections from the Family tab anytime.');
      } else if (connectionType === 'child') {
        await axios.post(`${API}/relationships/parent-child`, {
          tree_id: person.tree_id,
          parent_id: pid,
          child_id: selectedPersonId,
          relationship_type: connectionDetails.relationship_subtype,
          notes: connectionDetails.notes
        });
        toast.success('Child linked on the family tree. Add more connections from the Family tab anytime.');
      } else if (connectionType === 'sibling') {
        // Sibling: Both share the same parent
        // If current person has parents, use one as the shared parent
        // Otherwise, need to specify a shared parent
        const sharedParentId = connectionDetails.shared_parent_id || (relationships.parents[0]?.id);
        
        if (!sharedParentId) {
          toast.error('Please select a shared parent first');
          return;
        }
        
        // Make selected person also a child of the shared parent
        await axios.post(`${API}/relationships/parent-child`, {
          tree_id: person.tree_id,
          parent_id: sharedParentId,
          child_id: selectedPersonId,
          relationship_type: connectionDetails.relationship_subtype,
          notes: connectionDetails.notes
        });
        toast.success('Sibling linked on the family tree. Add more connections from the Family tab anytime.');
      } else if (connectionType === 'partner') {
        await axios.post(`${API}/relationships/partnership`, {
          tree_id: person.tree_id,
          partner1_id: pid,
          partner2_id: selectedPersonId,
          partnership_type: connectionDetails.partnership_type,
          start_date: connectionDetails.start_date,
          start_place: connectionDetails.start_place,
          notes: connectionDetails.notes
        });
        toast.success('Partner linked on the family tree. Add more connections from the Family tab anytime.');
      }
      
      // Clear tree cache so it regenerates with new relationships
      clearTreeCache(person.tree_id);
      
      setIsConnectionDialogOpen(false);
      setTimeout(() => fetchPersonData(), 500);
    } catch (error) {
      toast.error(humanizeError(error));
    }
  };

  // Archive person (soft delete, reversible)
  const handleArchive = async () => {
    setArchiving(true);
    try {
      await axios.post(`${API}/individuals/${personId}/archive`);
      clearTreeCache(person.tree_id);
      toast.success(`${person?.given_names} has been archived. You can restore them anytime.`);
      navigate(treeHref(tree || { id: person.tree_id }));
    } catch (error) {
      toast.error(humanizeError(error));
    } finally {
      setArchiving(false);
      setShowArchiveDialog(false);
    }
  };

  // Check what would be deleted before showing delete dialog
  const handleDeleteCheck = async () => {
    try {
      const response = await axios.get(`${API}/individuals/${personId}/can-delete`);
      setDeleteInfo(response.data);
      setShowDeleteDialog(true);
    } catch (error) {
      toast.error(humanizeError(error));
    }
  };

  // Permanent delete
  const handleDelete = async (force = false) => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/individuals/${personId}?force=${force}`);
      clearTreeCache(person.tree_id);
      toast.success(`${person?.given_names} has been permanently removed.`);
      navigate(treeHref(tree || { id: person.tree_id }));
    } catch (error) {
      toast.error(humanizeError(error));
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  // Remove a specific relationship
  const handleRemoveRelationship = async (relationshipId, relationshipType, personName) => {
    if (!window.confirm(`Remove the connection with ${personName}? This won't delete anyone, just unlinks them.`)) {
      return;
    }
    try {
      const endpoint = relationshipType === 'partnership' 
        ? `${API}/relationships/partnership/${relationshipId}`
        : `${API}/relationships/parent-child/${relationshipId}`;
      
      await axios.delete(endpoint);
      clearTreeCache(person.tree_id);
      toast.success('Connection removed');
      fetchPersonData();
    } catch (error) {
      toast.error(humanizeError(error));
    }
  };

  const getInitials = (p) => {
    return `${p?.given_names?.charAt(0) || ''}${p?.surname?.charAt(0) || ''}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingState message="Loading..." />
      </div>
    );
  }

  const canEdit = tree?.role === 'owner' || tree?.role === 'editor';
  const canManageMemorial = tree?.role === 'owner' || tree?.effective_permission === 'manage';

  const archivalViewRaw = searchParams.get('view') || 'overview';
  const archivalView = ['overview', 'stories', 'documents', 'shared', 'family'].includes(archivalViewRaw)
    ? archivalViewRaw
    : 'overview';

  const goArchivalView = (view, options = {}) => {
    const params = new URLSearchParams();
    params.set('view', view);
    if (options.focus) params.set('focus', options.focus);
    if (options.story) params.set('story', options.story);
    navigate(`${personHref(person)}?${params.toString()}`, { replace: true });
  };

  const handleProfilePromptNavigate = (view, options) => {
    if (view === 'reminders') {
      const el = document.querySelector('[data-testid="important-dates-section"], [data-testid="overview-important-dates"]');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      else navigate('/reminders');
      return;
    }
    if (view === 'markers') {
      const el = document.querySelector('[data-testid="priceless-possessions-section"], [data-testid="memory-markers-section"]');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    if (view === 'export') {
      handleExportPdf('keepsake');
      return;
    }
    goArchivalView(view, options);
  };

  const handleProfileIntentionChange = (intention) => {
    setProfileIntentionState(intention);
    if (person?.id) setProfileIntention(person.id, intention);
  };

  const heldHereNavigation = {
    onOpenStories: () => goArchivalView('stories'),
    onOpenVoice: () => goArchivalView('stories', { focus: 'voice' }),
    onOpenDocuments: () => goArchivalView('documents'),
  };

  const personWorkspaceContextStrip = (tabCaption) => (
    <PersonStudioHeader
      compact
      person={person}
      storyCount={storyCount}
      voiceMemoriesCount={voiceMemoriesCount}
      documentCount={documents.length}
      tabCaption={tabCaption}
      onOpenProfile={() => goArchivalView('overview')}
      {...heldHereNavigation}
    />
  );

  /** Keep the current archive tab when opening a related profile (nested Tabs under Family used to confuse Radix). */
  const navigateToRelatedPerson = (id) => {
    if (!id) return;
    const rel =
      allPeople.find((p) => p.id === id) ||
      (person?.id === id ? person : null) ||
      { id };
    navigate(`${personHref(rel)}?view=${archivalView}`);
  };

  const personProfileCardAside = (
      <Card data-testid="person-profile-card" className="border-border/60 shadow-sm">
        <CardContent className="pt-6">
          <div className="text-center">
            <div className="relative inline-block mb-4">
              <PersonAvatarWithIndicators
                data-testid="person-photo"
                photoUrl={
                  person?.profile_image_url
                    ? resolveAssetUrl(BACKEND_URL, person.profile_image_url)
                    : null
                }
                initials={getInitials(person)}
                size="xl"
                storyCount={storyCount}
                documentCount={documents.length}
                indicatorAlign="left"
              />

              {canEdit && (
                <div className="absolute -bottom-1 -right-1">
                  <label
                    className={`w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-md cursor-pointer hover:bg-primary/90 transition-colors ${uploadingPhoto ? 'opacity-50 cursor-wait' : ''}`}
                    title={person?.profile_image_url ? 'Change photo' : 'Add photo'}
                    data-testid="upload-photo-btn"
                  >
                    <Camera className="w-4 h-4" />
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={CUSTOMER_IMAGE_ACCEPT}
                      className="hidden"
                      onChange={handlePhotoUpload}
                      disabled={uploadingPhoto}
                    />
                  </label>
                </div>
              )}
            </div>

            {uploadingPhoto && <p className="text-xs text-muted-foreground mb-2">Uploading...</p>}

            <div className="mb-2 flex flex-wrap items-center justify-center gap-2">
              <CompanionBadge person={person} />
            </div>

            <h2 className="text-2xl font-serif font-light mb-1">
              {getDisplayName(person)}
            </h2>
            {person?.profile_type === PROFILE_TYPE.PET && person?.species ? (
              <p className="text-sm text-muted-foreground/70 mb-2 capitalize">
                {person.species}
                {person.breed ? ` · ${person.breed}` : ''}
              </p>
            ) : person?.family_connection ? (
              <p className="text-sm text-muted-foreground/70 mb-2">{person.family_connection}</p>
            ) : person?.sex && isGenealogical ? (
              <p className="text-sm text-muted-foreground/70 mb-2 capitalize">{person.sex}</p>
            ) : null}
            {isCompanionProfile(person) ? (
              <PetStoryAttachment
                pet={person}
                anchorPerson={attachedAnchorPerson}
                className="mb-3 text-center"
              />
            ) : null}
            {/* Connection status — replaces the old private-only badge with full status coverage */}
            <ConnectionStatusBadge person={person} className="mb-3" />
            {/* Request verification action — shown below the badge when editable and status allows it */}
            {canEdit && isGenealogical && ['private_placeholder', undefined].includes(person?.identity_status) && !isSelfPerson && (
              <div className="mb-3">
                <button
                  type="button"
                  className="w-full rounded-lg border border-dashed border-[hsl(38_14%_82%)]/80 px-3 py-2 text-[11px] text-muted-foreground/80 transition hover:border-primary/30 hover:text-primary/70"
                  onClick={async () => {
                    try {
                      const targetId = person?.id || personId;
                      const resp = await axios.patch(`${API}/individuals/${targetId}/request-verification`);
                      setPerson(resp.data);
                      toast.success('Marked as verification pending.', {
                        description: `${person?.given_names || 'This person'} can confirm their connection when they join MyLegacyBridge. Email invite coming soon.`,
                        duration: 6000,
                      });
                    } catch (err) {
                      const detail = err?.response?.data?.detail;
                      const msg =
                        detail === 'Person not found.' ||
                        (typeof detail === 'string' &&
                          detail.includes('could not request verification'))
                          ? 'We could not request verification for this profile yet. Please save the person first, then try again.'
                          : detail || 'Could not update verification status.';
                      toast.error(msg);
                    }
                  }}
                  data-testid="request-verification-btn"
                >
                  Request verification
                </button>
              </div>
            )}
            {isGenealogical ? (
              <div className="mx-auto max-w-[17rem] text-center space-y-2">
                {isSelfPerson ? (
                  <>
                    {/* "This is you" badge */}
                    <span
                      className="inline-flex items-center gap-1 rounded-full border border-primary/25 bg-primary/8 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary"
                      data-testid="this-is-you-badge"
                    >
                      <User className="h-3 w-3" aria-hidden /> This is you
                    </span>
                    <p className="text-[11px] leading-snug text-muted-foreground">
                      Linked as your archive profile. Use &quot;My profile&quot; on the dashboard or sidebar to return to your stories and documents.
                    </p>
                    <button
                      type="button"
                      className="text-[11px] text-muted-foreground underline underline-offset-2 hover:text-foreground transition-colors"
                      onClick={async () => {
                        try {
                          await clearSelfPersonOnServer();
                          await refreshUser();
                        } catch {
                          setMyArchivePersonId(null);
                        }
                        setLinkedAsMeInArchive(false);
                        toast.message('Archive profile link cleared.');
                      }}
                      data-testid="clear-archive-me-link"
                    >
                      Not me — clear link
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="text-xs font-medium text-[#5c4a32] underline decoration-[#c9a961]/45 underline-offset-4 transition hover:text-[#1c1917]"
                    onClick={async () => {
                      try {
                        await setSelfPersonOnServer(personId);
                        await refreshUser();
                      } catch {
                        setMyArchivePersonId(personId);
                      }
                      setLinkedAsMeInArchive(true);
                      toast.success('Saved. Your dashboard and archive menu now show your profile.', { duration: 4500 });
                    }}
                    data-testid="remember-as-me-btn"
                  >
                    This is me in the archive
                  </button>
                )}
              </div>
            ) : (
              <p className="mx-auto max-w-[17rem] text-center text-[11px] leading-snug text-muted-foreground">
                Preserved in your family archive — stories, photos, and documents can be added as you would for any loved one.
              </p>
            )}
          </div>

          <div className="space-y-4 mt-6 border-t border-border/40 pt-6">
            {person?.birth_date && (
              <div data-testid="person-birth-info">
                <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground/60 mb-1">
                  {getBirthDateLabel(person)}
                </p>
                <p className="font-medium">{formatDisplayDate(person.birth_date)}</p>
                {person.birth_place && <p className="text-sm text-muted-foreground">{person.birth_place}</p>}
              </div>
            )}
            {person?.death_date && (
              <div data-testid="person-death-info">
                <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground/60 mb-1">
                  {getDeathDateLabel(person)}
                </p>
                <p className="font-medium">{formatDisplayDate(person.death_date)}</p>
                {person.death_place && <p className="text-sm text-muted-foreground">{person.death_place}</p>}
              </div>
            )}
            {!person?.birth_date && !person?.death_date && (
              <p className="text-sm text-muted-foreground/60 text-center py-4">Birth and life dates can be added anytime.</p>
            )}
          </div>
        </CardContent>
      </Card>
  );

  const personInsightPanelProps = {
    person,
    activeTab: archivalView,
    storyCount,
    voiceMemoriesCount,
    documentCount: documents.length,
    possessionCount,
    familyConnectionCount,
    relationships,
    canEdit,
    isGenealogical,
    onNavigate: goArchivalView,
    onExportPdf: handleExportPdf,
    onOpenMemorial: () => navigate(memorialManageHref(person)),
    exportingPdf,
    collapsed: profileGuidance.collapsed,
    isWide: profileGuidance.isWide,
    onExpand: profileGuidance.expand,
    onCollapse: profileGuidance.collapse,
  };

  const sidebarInsightPanel = profileGuidance.isWide ? (
    <PersonProfileSidebarInsight {...personInsightPanelProps} />
  ) : null;
  const inlineInsightPanel = <PersonProfileInlineInsight {...personInsightPanelProps} />;

  return (
    <div className="flex min-h-screen bg-background">
      <ArchiveContextNav
        treeId={person.tree_id}
        treeLinkBase={tree ? treeHref(tree) : undefined}
        treeName={tree?.name}
        variant="person"
        personId={personId}
        personSummary={{
          given_names: person?.given_names,
          surname: person?.surname,
          profile_image_url: person?.profile_image_url,
        }}
        personHeldCounts={{
          storyCount,
          voiceMemoriesCount,
          documentCount: documents.length,
        }}
        onPersonViewNavigate={goArchivalView}
        canExport={Boolean(person?.tree_id)}
        peopleCount={1}
        onExportPdf={handleExportPdf}
        onExportJson={handleExportJson}
        exportingPdf={exportingPdf}
        exportingJson={exportingJson}
      />
      <div className="flex min-h-0 min-w-0 flex-1 flex-col pb-[calc(5.25rem+env(safe-area-inset-bottom,0px))] lg:pb-0">
      <nav className="border-b border-border/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => navigate('/home')}
                className="hidden text-lg transition-colors hover:opacity-85 sm:block"
                data-testid="home-link"
              >
                <BrandWordmark variant="treeNav" className="text-lg" />
              </button>
              <span className="text-muted-foreground/40 hidden sm:block">/</span>
              <Button variant="ghost" size="icon" onClick={() => navigate(treeHref(tree || { id: person?.tree_id }))} className="sm:hidden" data-testid="back-btn-mobile">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <h1 className="text-xl font-serif font-semibold" data-testid="person-name">{person?.given_names} {person?.surname}</h1>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <ThemeToggle />
              {canEdit && isGenealogical && (
                <Button
                  type="button"
                  size="sm"
                  className="rounded-full"
                  onClick={startAddConnection}
                  data-testid="header-add-connection-btn"
                >
                  <Link2 className="mr-1.5 h-4 w-4" aria-hidden />
                  <span className="hidden sm:inline">{ADD_CONNECTION}</span>
                  <span className="sm:hidden">Connect</span>
                </Button>
              )}
              {person?.tree_id ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      size="sm"
                      disabled={exportingPdf || exportingJson}
                      className="rounded-full border-emerald-700 bg-emerald-700 text-white hover:bg-emerald-800 hover:border-emerald-800"
                      data-testid="person-header-export-btn"
                    >
                      {exportingPdf || exportingJson ? (
                        <Loader2 className="h-4 w-4 animate-spin sm:mr-1.5" aria-hidden />
                      ) : (
                        <Download className="h-4 w-4 sm:mr-1.5" aria-hidden />
                      )}
                      <span className="hidden sm:inline">Export</span>
                      <ChevronDown className="ml-1 h-3.5 w-3.5 opacity-90 hidden sm:inline" aria-hidden />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem
                      onSelect={() => handleExportPdf('keepsake')}
                      disabled={exportingPdf}
                      data-testid="person-export-pdf"
                    >
                      <FileText className="mr-2 h-4 w-4" aria-hidden />
                      {LEGACY_BOOK_COPY.downloadKeepsake}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={() => handleExportPdf('review')}
                      disabled={exportingPdf}
                      data-testid="person-export-pdf-review"
                    >
                      <FileText className="mr-2 h-4 w-4" aria-hidden />
                      {LEGACY_BOOK_COPY.reviewDraftPdf}
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={handleOpenAudiobook} data-testid="person-export-audiobook">
                      <Headphones className="mr-2 h-4 w-4" aria-hidden />
                      Open audiobook
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={handleExportJson}
                      disabled={exportingJson}
                      data-testid="person-export-backup"
                    >
                      <FileJson className="mr-2 h-4 w-4" aria-hidden />
                      {LEGACY_BOOK_COPY.downloadBackup}
                    </DropdownMenuItem>
                    {isGenealogical ? (
                      <DropdownMenuItem
                        onSelect={() => navigate(memorialManageHref(person))}
                        data-testid="person-export-memorial"
                      >
                        <Flame className="mr-2 h-4 w-4" aria-hidden />
                        Memorial settings
                      </DropdownMenuItem>
                    ) : null}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : null}
              {canEdit && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-full"
                    onClick={() => setIsEditDialogOpen(true)}
                    data-testid="edit-person-btn"
                  >
                    <Edit className="mr-1.5 h-4 w-4" aria-hidden />
                    <span className="hidden sm:inline">Edit</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-full"
                    onClick={() => setShowArchiveDialog(true)}
                    data-testid="archive-person-btn"
                  >
                    <Archive className="mr-1.5 h-4 w-4" aria-hidden />
                    <span className="hidden sm:inline">Archive</span>
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      <div className="mx-auto flex-1 px-4 py-6 sm:px-6 md:py-10 lg:px-8 max-w-[min(100%,96rem)]">
        <Tabs
          key={`person-archival-${personId}`}
          value={archivalView}
          onValueChange={goArchivalView}
          className="w-full"
        >
          <TabsList className="mb-6 flex h-auto w-full flex-wrap justify-start gap-1 rounded-2xl bg-muted/40 p-1">
            <TabsTrigger value="overview" className="rounded-xl px-3 py-2 text-xs sm:text-sm">
              Overview
            </TabsTrigger>
            <TabsTrigger
              value="stories"
              title={
                person
                  ? `${CORE_JOURNEY_UI ? 'Memories & voice' : 'Stories & voice'} — ${[person.given_names, person.surname].filter(Boolean).join(' ')}`
                  : CORE_JOURNEY_UI ? 'Memories & voice' : 'Stories & voice'
              }
              className="rounded-xl px-3 py-2 text-xs sm:text-sm"
            >
              <span className="sm:hidden">{CORE_JOURNEY_UI ? 'Memories' : 'Stories'}</span>
              <span className="hidden sm:inline">
                {CORE_JOURNEY_UI ? 'Memories & voice' : 'Stories & voice'}
              </span>
            </TabsTrigger>
            <TabsTrigger value="documents" className="rounded-xl px-3 py-2 text-xs sm:text-sm">
              Documents
            </TabsTrigger>
            <TabsTrigger value="shared" className="rounded-xl px-3 py-2 text-xs sm:text-sm">
              Shared archive
            </TabsTrigger>
            <TabsTrigger value="family" className="rounded-xl px-3 py-2 text-xs sm:text-sm">
              Family
            </TabsTrigger>
          </TabsList>

          {archivalView === 'overview' && (
          <TabsContent value="overview" className="mt-0 outline-none">
            <PersonProfilePageLayout leftAside={personProfileCardAside} insightPanel={sidebarInsightPanel}>
              <div className="space-y-8">
                <ContextualGuideTip tipId="person-overview" title="Person archive home">
                  Add stories, dates, keepsakes and memories here — at your own pace.
                </ContextualGuideTip>

                {inlineInsightPanel}

                <PersonOverviewRichFields
                  person={person}
                  canEdit={canEdit}
                  sections={['summary']}
                  featuredMemoryPreview={featuredMemoryPreview}
                  onOpenStories={() => goArchivalView('stories')}
                  onUpdated={(data) => setPerson((prev) => ({ ...(prev || {}), ...data }))}
                />

                <PersonOverviewHub
                  person={person}
                  storyCount={storyCount}
                  voiceMemoriesCount={voiceMemoriesCount}
                  documentCount={documents.length}
                  possessionCount={possessionCount}
                  familyConnectionCount={familyConnectionCount}
                  canEdit={canEdit}
                  isGenealogical={isGenealogical}
                  onNavigate={goArchivalView}
                />

                <LegacyBookPersonSetting
                  person={person}
                  canEdit={canEdit}
                  onUpdated={(data) => setPerson((prev) => ({ ...(prev || {}), ...data }))}
                />

                <div className="space-y-7">
                  <div>
                    <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground/55">
                      Featured memories
                    </p>
                    <p className="mt-1 max-w-2xl text-sm leading-relaxed text-muted-foreground/80">
                      What they were known for, what they loved, and a line worth remembering.
                    </p>
                  </div>
                  <PersonOverviewRichFields
                    person={person}
                    canEdit={canEdit}
                    sections={['themes', 'featured']}
                    featuredMemoryPreview={featuredMemoryPreview}
                    onOpenStories={() => goArchivalView('stories')}
                    onUpdated={(data) => setPerson((prev) => ({ ...(prev || {}), ...data }))}
                  />
                  <WhatTheyLovedSection
                    personId={person?.id}
                    items={Array.isArray(person?.loved_items) ? person.loved_items : []}
                    canEdit={canEdit}
                    onUpdated={(loved_items) => setPerson((prev) => ({ ...(prev || {}), loved_items }))}
                  />
                </div>

                <PersonOverviewOutputsSection
                  person={person}
                  treeId={person?.tree_id}
                  canEdit={canEdit}
                  isGenealogical={isGenealogical}
                  canManageMemorial={canManageMemorial}
                  onExportPdf={handleExportPdf}
                  exportingPdf={exportingPdf}
                  onNavigate={goArchivalView}
                  personStories={personStories}
                  storyCount={storyCount}
                  documentCount={documents.length}
                  onManageTribute={canManageMemorial ? () => setShowMemorialVisibility(true) : undefined}
                />

                <section
                  className="space-y-6 border-t border-[hsl(38_14%_86%)]/50 pt-8"
                  data-testid="person-overview-more-details"
                >
                  <div>
                    <p className="text-[10px] font-mono uppercase tracking-[0.16em] text-muted-foreground/55">
                      More details
                    </p>
                    <p className="mt-1 max-w-2xl text-sm leading-relaxed text-muted-foreground/80">
                      Dates, links, connections, and keepsakes linked to this profile.
                    </p>
                  </div>

                  {canEdit && isGenealogical ? (
                    <ProfileIntentionPicker
                      value={profileIntention}
                      onChange={handleProfileIntentionChange}
                      compact
                      className="rounded-xl border border-[hsl(38_14%_86%)]/60 bg-[hsl(42_28%_99%)]/50 px-4 py-4"
                    />
                  ) : null}

                  {isGenealogical && profileIntention === PROFILE_INTENTION.FIRST_CHAPTER ? (
                    <FirstChapterProfileCard
                      person={person}
                      canEdit={canEdit}
                      hasProfilePhoto={Boolean(person?.profile_image_url)}
                      storyCount={storyCount}
                      voiceMemoriesCount={voiceMemoriesCount}
                      documentCount={documents.length}
                      reminderCount={personReminderCount}
                      onNavigate={handleProfilePromptNavigate}
                    />
                  ) : null}

                  {(isGenealogical || canEdit) && (person?.birth_date || person?.death_date || canEdit) && (
                    <section data-testid="overview-important-dates" aria-label="Important dates">
                      <ImportantDatesSection person={person} treeId={person?.tree_id} canEdit={canEdit} />
                    </section>
                  )}

                  {(canEdit || hasVideoLink(person)) && (
                    <section data-testid="overview-video-memory" aria-label="Video memory">
                      <VideoLinkAttach
                        entity={person}
                        entityLabel="profile"
                        canEdit={canEdit}
                        apiBase={API}
                        putPath={`/individuals/${person.id}/video-link`}
                        deletePath={`/individuals/${person.id}/video-link`}
                        onUpdated={() => fetchPersonData()}
                      />
                    </section>
                  )}

                  <ProfileLinksSection
                    personId={person?.id}
                    links={Array.isArray(person?.profile_links) ? person.profile_links : []}
                    canEdit={canEdit}
                    onUpdated={(profile_links) => setPerson((prev) => ({ ...(prev || {}), profile_links }))}
                  />

                  {isGenealogical ? (
                    <MemoryMarkersSection
                      treeId={person?.tree_id}
                      personId={person?.id}
                      attachedPets={attachedPetsForPerson}
                      canEdit={canEdit}
                      anchorPerson={person}
                      people={allPeople}
                      stories={personStories}
                    />
                  ) : null}

                  {canEdit && person?.tree_id && isGenealogical && (
                    <div className="max-w-xl space-y-3 rounded-xl border border-[hsl(38_14%_86%)]/75 bg-[hsl(42_28%_99%)]/55 px-4 py-4">
                      <p className="text-sm leading-relaxed text-muted-foreground">{GROW_BRANCH_COPY}</p>
                      <div className="flex flex-wrap gap-2">
                        <Button type="button" size="sm" className="rounded-full" onClick={startAddConnection} data-testid="overview-link-connection-btn">
                          <Link2 className="mr-2 h-4 w-4" aria-hidden />
                          {ADD_CONNECTION}
                        </Button>
                        <Button variant="outline" size="sm" className="rounded-full" asChild>
                          <Link
                            to={buildTreePeopleAddPersonUrl(tree ? { id: person.tree_id, slug: tree.slug } : person.tree_id)}
                            data-testid="overview-add-person-btn"
                          >
                            <Plus className="mr-2 h-4 w-4" aria-hidden />
                            Add new person
                          </Link>
                        </Button>
                      </div>
                      <ArchiveConnectionPrivacyNotice compact />
                    </div>
                  )}
                </section>
              </div>
            </PersonProfilePageLayout>
          </TabsContent>
          )}

          {archivalView === 'family' && (
          <TabsContent value="family" className="mt-0 outline-none">
            <PersonProfilePageLayout insightPanel={sidebarInsightPanel}>
            <div className="min-w-0 space-y-4">
              {inlineInsightPanel}
              {personWorkspaceContextStrip('Family connections')}
              <div className="min-w-0">
            {!isGenealogical ? (
              <Card>
                <CardHeader>
                  <CardTitle className="font-serif">In your family archive</CardTitle>
                  <CardDescription>
                    Chosen family and pets are preserved here with stories, photos, and documents, separate from the genealogical tree.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3 text-sm text-muted-foreground leading-relaxed">
                  <PetStoryAttachment pet={person} anchorPerson={attachedAnchorPerson} />
                  {person?.family_connection ? (
                    <p>
                      <span className="font-medium text-foreground/85">Connection: </span>
                      {person.family_connection}
                    </p>
                  ) : null}
                  <p>
                    Genealogical parent, partner, and child links apply to people in the family tree. You can still mention this profile in stories and shared documents.
                  </p>
                </CardContent>
              </Card>
            ) : (
            <Card>
              <CardHeader>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <CardTitle className="font-serif">{FAMILY_CONNECTIONS}</CardTitle>
                    <CardDescription>Parents, partners, and children on the family tree</CardDescription>
                  </div>
                  {canEdit && (
                    <div className="flex flex-wrap gap-2 shrink-0">
                      <Button size="sm" className="rounded-full shadow-sm" onClick={startAddConnection} data-testid="add-connection-btn">
                        <Link2 className="mr-2 h-4 w-4" aria-hidden />
                        {ADD_CONNECTION}
                      </Button>
                      <Button variant="outline" size="sm" className="rounded-full border-[hsl(38_14%_82%)] text-muted-foreground hover:text-foreground" asChild>
                        <Link
                          to={buildTreePeopleAddPersonUrl(
                            tree ? { id: person.tree_id, slug: tree.slug } : person?.tree_id
                          )}
                          data-testid="family-add-connection-new-btn"
                        >
                          <Plus className="mr-2 h-4 w-4" /> Add new connection
                        </Link>
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {/* Relationship validation highlight banner */}
                {highlightRelationship && (
                  <div className="mb-4 p-3 bg-amber-900/20 border border-amber-700/50 rounded-lg flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                    <p className="text-sm text-amber-200/90">
                      Reviewing relationships for validation issue. Check highlighted connections below.
                    </p>
                  </div>
                )}
                
                {/* Use buttons + panels instead of nested Radix Tabs (nested Tabs can steal updates from the parent archival Tabs). */}
                <div
                  role="tablist"
                  aria-label="Relationship kinds"
                  className="grid w-full grid-cols-2 gap-1 rounded-lg bg-muted p-1 sm:grid-cols-4"
                >
                  {(['parents', 'partners', 'children', 'siblings']).map((tab) => {
                    const labels = {
                      parents: `Parents (${relationships.parents.length})`,
                      partners: `Partners (${relationships.partnerships.length})`,
                      children: `Children (${relationships.children.length})`,
                      siblings: `Siblings (${relationships.siblings.length})`,
                    };
                    const isActive = relationshipTab === tab;
                    return (
                      <button
                        key={tab}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        className={`inline-flex items-center justify-center whitespace-nowrap rounded-md px-2 py-1.5 text-xs font-medium ring-offset-background transition-all sm:px-3 sm:text-sm ${
                          isActive
                            ? 'bg-background text-foreground shadow'
                            : 'text-muted-foreground hover:bg-background/60'
                        }`}
                        onClick={() => setRelationshipTab(tab)}
                      >
                        {labels[tab]}
                      </button>
                    );
                  })}
                </div>

                {relationshipTab === 'parents' && (
                  <div className="mt-4 space-y-3" role="tabpanel">
                    {relationships.parents.length === 0 ? (
                      <div className="text-center py-8">
                        <User className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">Parents can be linked when you&apos;re ready.</p>
                        {canEdit && (
                          <Button variant="link" onClick={startAddConnection} className="mt-2">Add a parent</Button>
                        )}
                      </div>
                    ) : (
                      relationships.parents.map(parent => (
                        <div 
                          key={parent.id} 
                          className={`flex items-center gap-3 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-all duration-300 ${
                            highlightRelationship && (highlightedPersonId === parent.id || !highlightedPersonId)
                              ? 'border-amber-500/70 bg-amber-900/20 ring-2 ring-amber-500/30'
                              : ''
                          }`}
                          onClick={() => navigateToRelatedPerson(parent.id)}
                        >
                          <div className="w-12 h-12 rounded-full bg-secondary/50 flex items-center justify-center flex-shrink-0 overflow-hidden border border-border/40">
                            {parent.profile_image_url ? (
                              <img src={resolveAssetUrl(BACKEND_URL, parent.profile_image_url)} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <span className="font-serif text-primary/70">{parent.given_names?.charAt(0)}{parent.surname?.charAt(0)}</span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium">{parent.given_names} {parent.surname}</p>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <p className="text-xs text-muted-foreground/70 capitalize">{parent.relationship_type} parent</p>
                              <ConnectionStatusPill status={parent.identity_status} />
                            </div>
                          </div>
                          {highlightRelationship && (highlightedPersonId === parent.id || !highlightedPersonId) && (
                            <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}

                {relationshipTab === 'partners' && (
                  <div className="mt-4 space-y-3" role="tabpanel">
                    {relationships.partnerships.length === 0 ? (
                      <div className="text-center py-8">
                        <Heart className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">Partners can be linked when you&apos;re ready.</p>
                        {canEdit && (
                          <Button variant="link" onClick={startAddConnection} className="mt-2">Add a partner</Button>
                        )}
                      </div>
                    ) : (
                      relationships.partnerships.map(partner => (
                        <div 
                          key={partner.id} 
                          className={`flex items-center gap-3 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-all duration-300 ${
                            highlightRelationship && (highlightedPersonId === partner.id || !highlightedPersonId)
                              ? 'border-amber-500/70 bg-amber-900/20 ring-2 ring-amber-500/30'
                              : ''
                          }`}
                          onClick={() => navigateToRelatedPerson(partner.id)}
                        >
                          <div className="w-12 h-12 rounded-full bg-secondary/50 flex items-center justify-center flex-shrink-0 overflow-hidden border border-border/40">
                            {partner.profile_image_url ? (
                              <img src={resolveAssetUrl(BACKEND_URL, partner.profile_image_url)} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <span className="font-serif text-primary/70">{partner.given_names?.charAt(0)}{partner.surname?.charAt(0)}</span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium">{partner.given_names} {partner.surname}</p>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <p className="text-xs text-muted-foreground/70 capitalize">{partner.partnership_type?.replace('_', ' ')}</p>
                              <ConnectionStatusPill status={partner.identity_status} />
                            </div>
                            {partner.start_date && <p className="text-xs text-muted-foreground/60">Since {partner.start_date}</p>}
                          </div>
                          {highlightRelationship && (highlightedPersonId === partner.id || !highlightedPersonId) && (
                            <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}

                {relationshipTab === 'children' && (
                  <div className="mt-4 space-y-3" role="tabpanel">
                    {relationships.children.length === 0 ? (
                      <div className="text-center py-8">
                        <Users className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">Children and descendants can be added here.</p>
                        {canEdit && (
                          <Button variant="link" onClick={startAddConnection} className="mt-2">Add a child</Button>
                        )}
                      </div>
                    ) : (
                      relationships.children.map(child => (
                        <div 
                          key={child.id} 
                          className={`flex items-center gap-3 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-all duration-300 ${
                            highlightRelationship && (highlightedPersonId === child.id || !highlightedPersonId)
                              ? 'border-amber-500/70 bg-amber-900/20 ring-2 ring-amber-500/30'
                              : ''
                          }`}
                          onClick={() => navigateToRelatedPerson(child.id)}
                        >
                          <div className="w-12 h-12 rounded-full bg-secondary/50 flex items-center justify-center flex-shrink-0 overflow-hidden border border-border/40">
                            {child.profile_image_url ? (
                              <img src={resolveAssetUrl(BACKEND_URL, child.profile_image_url)} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <span className="font-serif text-primary/70">{child.given_names?.charAt(0)}{child.surname?.charAt(0)}</span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium">{child.given_names} {child.surname}</p>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <p className="text-xs text-muted-foreground/70 capitalize">{child.relationship_type} child</p>
                              <ConnectionStatusPill status={child.identity_status} />
                            </div>
                          </div>
                          {highlightRelationship && (highlightedPersonId === child.id || !highlightedPersonId) && (
                            <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}

                {relationshipTab === 'siblings' && (
                  <div className="mt-4 space-y-3" role="tabpanel">
                    {relationships.siblings.length === 0 ? (
                      <div className="text-center py-8">
                        <Users className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">Brothers, sisters, and siblings belong here.</p>
                        {canEdit && (
                          <Button variant="link" onClick={() => { startAddConnection(); setConnectionType('sibling'); }} className="mt-2">
                            Add a sibling
                          </Button>
                        )}
                      </div>
                    ) : (
                      relationships.siblings.map(sibling => (
                        <div 
                          key={sibling.id} 
                          className={`flex items-center gap-3 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-all duration-300 ${
                            highlightRelationship && (highlightedPersonId === sibling.id || !highlightedPersonId)
                              ? 'border-amber-500/70 bg-amber-900/20 ring-2 ring-amber-500/30'
                              : ''
                          }`}
                          onClick={() => navigateToRelatedPerson(sibling.id)}
                        >
                          <div className="w-12 h-12 rounded-full bg-secondary/50 flex items-center justify-center flex-shrink-0 overflow-hidden border border-border/40">
                            {sibling.profile_image_url ? (
                              <img src={resolveAssetUrl(BACKEND_URL, sibling.profile_image_url)} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <span className="font-serif text-primary/70">{sibling.given_names?.charAt(0)}{sibling.surname?.charAt(0)}</span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium">{sibling.given_names} {sibling.surname}</p>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <p className="text-xs text-muted-foreground/70 capitalize">{sibling.relationship_type} sibling</p>
                              <ConnectionStatusPill status={sibling.identity_status} />
                            </div>
                          </div>
                          {highlightRelationship && (highlightedPersonId === sibling.id || !highlightedPersonId) && (
                            <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
            )}
              </div>
            </div>
            </PersonProfilePageLayout>
          </TabsContent>
          )}

          {archivalView === 'stories' && (
          <TabsContent value="stories" className="mt-0 outline-none">
            <PersonProfilePageLayout insightPanel={sidebarInsightPanel}>
            <div className="min-w-0 space-y-3">
              {inlineInsightPanel}
              {personWorkspaceContextStrip(CORE_JOURNEY_UI ? 'Memories & voice' : 'Stories & voice')}
              <ContextualGuideTip tipId="stories-voice">
                Record, transcribe or write memories. You can polish them into narrative later.
              </ContextualGuideTip>
              <StoriesTab
                personId={personId}
                treeId={person?.tree_id}
                canEdit={canEdit}
                mentionedStories={mentionedStories}
                initialStoryId={searchParams.get('story')}
                initialFocus={searchParams.get('focus')}
                editorFirst
                subjectIdentityAttachment={false}
                subject={{
                  given_names: person?.given_names,
                  surname: person?.surname,
                  profile_image_url: person?.profile_image_url,
                }}
              />
            </div>
            </PersonProfilePageLayout>
          </TabsContent>
          )}

          {archivalView === 'documents' && (
          <TabsContent value="documents" className="mt-0 outline-none">
            <PersonProfilePageLayout insightPanel={sidebarInsightPanel}>
            <div className="min-w-0 space-y-4">
              {inlineInsightPanel}
              {personWorkspaceContextStrip('Documents')}
              <DocumentsTab
                personId={personId}
                personName={person?.given_names}
                documents={documents}
                onDocumentsChange={fetchPersonData}
                canEdit={canEdit}
              />
            </div>
            </PersonProfilePageLayout>
          </TabsContent>
          )}

          {archivalView === 'shared' && (
          <TabsContent value="shared" className="mt-0 outline-none">
            <PersonProfilePageLayout insightPanel={sidebarInsightPanel}>
            <div className="min-w-0 space-y-4">
              {inlineInsightPanel}
              {personWorkspaceContextStrip('Shared family archive')}
              <div className="min-w-0">
            <Card className="border-[hsl(38_14%_86%)] bg-[hsl(42_28%_99%)]">
              <CardHeader>
                <CardTitle className="font-serif">Shared family archive</CardTitle>
                <CardDescription>
                  Documents for the whole family — not tied to one person. Upload from{' '}
                  <button
                    type="button"
                    className="text-primary underline-offset-4 hover:underline"
                    onClick={() => goArchivalView('documents')}
                  >
                    Documents
                  </button>{' '}
                  and choose &quot;Shared family archive&quot; when asked where the file should live.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 text-sm text-muted-foreground">
                {sharedDocuments.length === 0 ? (
                  <p>Nothing shared yet. When you preserve something for everyone in this archive, it will appear here.</p>
                ) : (
                  <ul className="divide-y divide-border/50 rounded-xl border border-border/40 bg-card/40">
                    {sharedDocuments.map((d) => (
                      <li key={d.id} className="flex items-center justify-between gap-3 px-4 py-3">
                        <div className="min-w-0">
                          <p className="truncate font-medium text-foreground">{d.title}</p>
                          <p className="text-xs capitalize">{d.document_type?.replace(/_/g, ' ') || 'Document'}</p>
                        </div>
                        <Button variant="outline" size="sm" className="shrink-0 rounded-full" asChild>
                          <a href={resolveAssetUrl(BACKEND_URL, d.file_url)} target="_blank" rel="noopener noreferrer">
                            Open
                          </a>
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
              </div>
            </div>
            </PersonProfilePageLayout>
          </TabsContent>
          )}

        </Tabs>
      </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Information</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className={editProfileType === PROFILE_TYPE.PET ? 'col-span-2' : ''}>
              <Label>{editProfileType === PROFILE_TYPE.PET ? 'Name' : 'First Name(s)'}</Label>
              <Input value={editData.given_names || ''} onChange={(e) => setEditData({...editData, given_names: e.target.value})} />
            </div>
            {editProfileType === PROFILE_TYPE.PERSON ? (
              <div>
                <Label>Last Name</Label>
                <Input value={editData.surname || ''} onChange={(e) => setEditData({...editData, surname: e.target.value})} />
              </div>
            ) : editProfileType === PROFILE_TYPE.COMPANION ? (
              <div>
                <Label>Last name (optional)</Label>
                <Input value={editData.surname || ''} onChange={(e) => setEditData({...editData, surname: e.target.value})} />
              </div>
            ) : null}
            {editProfileType === PROFILE_TYPE.PET ? (
              <>
                <div>
                  <Label>Species</Label>
                  <Select
                    value={editData.species ?? undefined}
                    onValueChange={(value) => setEditData({ ...editData, species: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Species" />
                    </SelectTrigger>
                    <SelectContent>
                      {PET_SPECIES.map((s) => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Breed (optional)</Label>
                  <Input value={editData.breed || ''} onChange={(e) => setEditData({ ...editData, breed: e.target.value })} />
                </div>
              </>
            ) : null}
            {editProfileType === PROFILE_TYPE.COMPANION ? (
              <>
                <div className="col-span-2">
                  <Label>Connection to your family</Label>
                  <Select
                    value={editData.companion_type ?? undefined}
                    onValueChange={(value) => setEditData({ ...editData, companion_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Connection type" />
                    </SelectTrigger>
                    <SelectContent>
                      {COMPANION_TYPE_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Connection to the family</Label>
                  <Input
                    value={editData.family_connection || ''}
                    onChange={(e) => setEditData({ ...editData, family_connection: e.target.value })}
                  />
                  </div>
                </>
              ) : null}
              {(editProfileType === PROFILE_TYPE.PET || editProfileType === PROFILE_TYPE.COMPANION) ? (
                <div>
                  <Label>Part of someone&apos;s story (optional)</Label>
                  <Select
                    value={editData.attached_to_person_id || '__none__'}
                    onValueChange={(value) =>
                      setEditData({
                        ...editData,
                        attached_to_person_id: value === '__none__' ? null : value,
                      })
                    }
                  >
                    <SelectTrigger data-testid="attached-person-select">
                      <SelectValue placeholder="Choose a person in the family tree" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Not linked to a person</SelectItem>
                      {linkablePeople
                        .filter((p) => p.id !== personId)
                        .map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.given_names} {p.surname}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Shows as &ldquo;Part of [Name]&rsquo;s story&rdquo; on this profile — pets stay separate from the genealogical tree.
                  </p>
                </div>
              ) : null}
              <div>
                <Label>{getBirthDateLabel(editData)}</Label>
              <SmartDateInput
                mode="simple"
                value={editData.birth_date || ''}
                onChange={(v) => setEditData({ ...editData, birth_date: v })}
              />
            </div>
            <div>
              <Label>{editProfileType === PROFILE_TYPE.PET ? 'Joined family at' : 'Birth place'}</Label>
              <Input value={editData.birth_place || ''} onChange={(e) => setEditData({...editData, birth_place: e.target.value})} />
            </div>
            <div>
              <Label>{getDeathDateLabel(editData)}</Label>
              <SmartDateInput
                mode="simple"
                value={editData.death_date || ''}
                onChange={(v) => setEditData({ ...editData, death_date: v })}
              />
            </div>
            <div>
              <Label>Death place</Label>
              <Input value={editData.death_place || ''} onChange={(e) => setEditData({...editData, death_place: e.target.value})} />
            </div>
            <div className="col-span-2">
              <Label>{isCompanionProfile(editData) ? getBiographicalNotesLabel(editData) : 'Life summary'}</Label>
              <p className="text-xs text-muted-foreground mt-1 mb-2">
                A short, warm introduction — who they were, where life took them, what stays with the family.
              </p>
              <Textarea
                value={editData.biographical_notes || ''}
                onChange={(e) => setEditData({ ...editData, biographical_notes: e.target.value })}
                rows={5}
                className="font-serif"
              />
            </div>
            <div className="col-span-2">
              <Label>Known for (one theme per line)</Label>
              <p className="text-xs text-muted-foreground mt-1 mb-2">
                Quiet fragments — traditions, crafts, music, service — not social &quot;interests.&quot;
              </p>
              <Textarea
                value={lifeThemesDraft}
                onChange={(e) => setLifeThemesDraft(e.target.value)}
                rows={3}
                placeholder={'Family dinners\nGardening\nHandwritten letters'}
                className="font-serif text-sm"
              />
            </div>
            <div className="col-span-2">
              <Label>Featured memory snippet (optional)</Label>
              <p className="text-xs text-muted-foreground mt-1 mb-2">
                Overrides the automatic line pulled from the first story with text. Leave blank to use the archive.
              </p>
              <Textarea
                value={editData.featured_memory_excerpt || ''}
                onChange={(e) => setEditData({ ...editData, featured_memory_excerpt: e.target.value })}
                rows={2}
                maxLength={400}
                className="font-serif text-sm"
              />
            </div>
            <div className="col-span-2">
              <MemorialRemembranceSection
                personId={person?.id}
                person={person}
                canManage={canManageMemorial}
                deathDate={editData.death_date || ''}
                onDeathDateChange={(v) => setEditData({ ...editData, death_date: v })}
                onSaved={() => fetchPersonData()}
                onOpenVisibility={() => setShowMemorialVisibility(true)}
              />
            </div>
            <div className="col-span-2">
              <ArchiveSlugEditor
                entityType="individual"
                entityId={person?.id}
                currentSlug={person?.slug || editData.slug}
                canEdit={canEdit}
                onSaved={(slug) => {
                  setEditData((d) => ({ ...d, slug }));
                  fetchPersonData();
                }}
              />
            </div>
            <div className="col-span-2 border-t border-border/40 pt-4">
              <ArchivePersonalizationSettings />
            </div>
          </div>
          <DialogFooter className="flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            {canEdit ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-foreground"
                onClick={() => {
                  setIsEditDialogOpen(false);
                  setShowArchiveDialog(true);
                }}
                data-testid="edit-dialog-archive-btn"
              >
                <Archive className="mr-1.5 h-4 w-4" aria-hidden />
                Archive profile
              </Button>
            ) : (
              <span />
            )}
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdate}>Save Changes</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MemorialVisibilityDialog
        open={showMemorialVisibility}
        onOpenChange={setShowMemorialVisibility}
        personId={person?.id}
        person={person}
        stories={personStories}
        onSaved={() => fetchPersonData()}
      />

      {/* Add Connection Dialog - Multi-step */}
      <Dialog open={isConnectionDialogOpen} onOpenChange={(open) => {
        setIsConnectionDialogOpen(open);
        if (!open) {
          setConnectionStep(1);
          setConnectionType('');
          setSelectedPersonId('');
        }
      }}>
        <DialogContent className="max-w-lg" data-testid="add-connection-dialog">
          <DialogHeader>
            <DialogTitle>{ADD_CONNECTION}</DialogTitle>
            <DialogDescription>
              {connectionStep === 1 && 'Choose how they relate on the family tree'}
              {connectionStep === 2 && 'Select someone already in your archive'}
              {connectionStep === 3 && 'Add details (optional)'}
            </DialogDescription>
          </DialogHeader>

          {connectionStep === 1 && (
            <div className="space-y-3 py-4">
              <ArchiveConnectionPrivacyNotice compact className="mb-1" />
              <p className="text-sm text-muted-foreground mb-4">Who is this person in relation to {person?.given_names}?</p>
              <Button
                variant={connectionType === 'parent' ? 'default' : 'outline'}
                className="w-full justify-start h-auto py-4"
                onClick={() => setConnectionType('parent')}
                data-testid="select-parent-type"
              >
                <div className="text-left">
                  <p className="font-semibold">Parent</p>
                  <p className="text-xs opacity-80">This person is {person?.given_names}&apos;s parent</p>
                </div>
              </Button>
              <Button
                variant={connectionType === 'child' ? 'default' : 'outline'}
                className="w-full justify-start h-auto py-4"
                onClick={() => setConnectionType('child')}
                data-testid="select-child-type"
              >
                <div className="text-left">
                  <p className="font-semibold">Child</p>
                  <p className="text-xs opacity-80">{person?.given_names} is this person&apos;s parent</p>
                </div>
              </Button>
              <Button
                variant={connectionType === 'sibling' ? 'default' : 'outline'}
                className="w-full justify-start h-auto py-4"
                onClick={() => setConnectionType('sibling')}
                data-testid="select-sibling-type"
              >
                <div className="text-left">
                  <p className="font-semibold">Sibling</p>
                  <p className="text-xs opacity-80">This person is {person?.given_names}&apos;s brother or sister</p>
                </div>
              </Button>
              <Button
                variant={connectionType === 'partner' ? 'default' : 'outline'}
                className="w-full justify-start h-auto py-4"
                onClick={() => setConnectionType('partner')}
                data-testid="select-partner-type"
              >
                <div className="text-left">
                  <p className="font-semibold">Partner / Spouse</p>
                  <p className="text-xs opacity-80">This person is {person?.given_names}&apos;s partner</p>
                </div>
              </Button>
            </div>
          )}

          {connectionStep === 2 && (
            <div className="space-y-4 py-4">
              <p className="text-xs leading-relaxed text-muted-foreground">{LINK_EXISTING_CONNECTION_HINT}</p>
              <div>
                <Label>Select Person</Label>
                <Select value={selectedPersonId} onValueChange={setSelectedPersonId}>
                  <SelectTrigger data-testid="select-person-dropdown" className="h-auto min-h-10 py-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      {selectedPersonId && (() => {
                        const p = allPeople.find((x) => x.id === selectedPersonId);
                        if (!p) return null;
                        return (
                          <PersonAvatarWithIndicators
                            photoUrl={
                              p.profile_image_url ? resolveAssetUrl(BACKEND_URL, p.profile_image_url) : null
                            }
                            initials={getInitials(p)}
                            size="xs"
                            storyCount={treePersonActivity.story[p.id] ?? 0}
                            documentCount={treePersonActivity.doc[p.id] ?? 0}
                          />
                        );
                      })()}
                      <SelectValue
                        placeholder="Choose from your family tree..."
                        className="truncate text-left"
                      />
                    </div>
                  </SelectTrigger>
                  <SelectContent className="max-h-[min(60vh,320px)]">
                    {linkablePeople
                      .filter((p) => p.id !== canonicalPersonId)
                      .map((p) => (
                      <SelectItem
                        key={p.id}
                        value={p.id}
                        textValue={
                          `${p.given_names || ''} ${p.surname || ''}`.trim() || 'Unknown'
                        }
                        className="py-2 pl-2 pr-8"
                      >
                        <PersonSelectRow
                          person={p}
                          photoBaseUrl={BACKEND_URL}
                          size="sm"
                          storyCount={treePersonActivity.story[p.id] ?? 0}
                          documentCount={treePersonActivity.doc[p.id] ?? 0}
                        />
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {connectionStep === 3 && (
            <div className="space-y-4 py-4">
              {(connectionType === 'parent' || connectionType === 'child') && (
                <div>
                  <Label>Relationship Type</Label>
                  <Select 
                    value={connectionDetails.relationship_subtype} 
                    onValueChange={(value) => setConnectionDetails({...connectionDetails, relationship_subtype: value})}
                  >
                    <SelectTrigger data-testid="relationship-subtype-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="biological">Biological</SelectItem>
                      <SelectItem value="adopted">Adopted</SelectItem>
                      <SelectItem value="foster">Foster</SelectItem>
                      <SelectItem value="step">Step</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {connectionType === 'sibling' && (
                <div className="space-y-4">
                  <div>
                    <Label>Shared Parent</Label>
                    <p className="text-xs text-muted-foreground mb-2">Who is the parent they share with {person?.given_names}?</p>
                    <Select 
                      value={connectionDetails.shared_parent_id || (relationships.parents[0]?.id || '')} 
                      onValueChange={(value) => setConnectionDetails({...connectionDetails, shared_parent_id: value})}
                    >
                      <SelectTrigger data-testid="shared-parent-select" className="h-auto min-h-10 py-2">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          {(() => {
                            const sid =
                              connectionDetails.shared_parent_id ||
                              relationships.parents[0]?.id ||
                              '';
                            const p = sid
                              ? allPeople.find((x) => x.id === sid) ||
                                relationships.parents.find((x) => x.id === sid)
                              : null;
                            if (!p) return null;
                            return (
                              <PersonAvatarWithIndicators
                                photoUrl={
                                  p.profile_image_url
                                    ? resolveAssetUrl(BACKEND_URL, p.profile_image_url)
                                    : null
                                }
                                initials={getInitials(p)}
                                size="xs"
                                storyCount={treePersonActivity.story[p.id] ?? 0}
                                documentCount={treePersonActivity.doc[p.id] ?? 0}
                              />
                            );
                          })()}
                          <SelectValue
                            placeholder="Select shared parent..."
                            className="truncate text-left"
                          />
                        </div>
                      </SelectTrigger>
                      <SelectContent className="max-h-[min(60vh,320px)]">
                        {relationships.parents.length > 0 ? (
                          <>
                            {relationships.parents.map((parent) => (
                              <SelectItem
                                key={parent.id}
                                value={parent.id}
                                textValue={
                                  `${parent.given_names || ''} ${parent.surname || ''}`.trim() ||
                                  'Unknown'
                                }
                                className="py-2 pl-2 pr-8"
                              >
                                <PersonSelectRow
                                  person={parent}
                                  photoBaseUrl={BACKEND_URL}
                                  size="sm"
                                  subtitle="Existing parent"
                                  storyCount={treePersonActivity.story[parent.id] ?? 0}
                                  documentCount={treePersonActivity.doc[parent.id] ?? 0}
                                />
                              </SelectItem>
                            ))}
                            <SelectItem value="__other__" disabled className="text-muted-foreground">
                              — or select another person —
                            </SelectItem>
                            {linkablePeople
                              .filter(
                                (p) =>
                                  !relationships.parents.find((par) => par.id === p.id) &&
                                  p.id !== canonicalPersonId &&
                                  p.id !== selectedPersonId
                              )
                              .map((p) => (
                                <SelectItem
                                  key={p.id}
                                  value={p.id}
                                  textValue={
                                    `${p.given_names || ''} ${p.surname || ''}`.trim() ||
                                    'Unknown'
                                  }
                                  className="py-2 pl-2 pr-8"
                                >
                                  <PersonSelectRow
                                    person={p}
                                    photoBaseUrl={BACKEND_URL}
                                    size="sm"
                                    storyCount={treePersonActivity.story[p.id] ?? 0}
                                    documentCount={treePersonActivity.doc[p.id] ?? 0}
                                  />
                                </SelectItem>
                              ))}
                          </>
                        ) : (
                          linkablePeople
                            .filter((p) => p.id !== canonicalPersonId && p.id !== selectedPersonId)
                            .map((p) => (
                              <SelectItem
                                key={p.id}
                                value={p.id}
                                textValue={
                                  `${p.given_names || ''} ${p.surname || ''}`.trim() ||
                                  'Unknown'
                                }
                                className="py-2 pl-2 pr-8"
                              >
                                <PersonSelectRow
                                  person={p}
                                  photoBaseUrl={BACKEND_URL}
                                  size="sm"
                                  storyCount={treePersonActivity.story[p.id] ?? 0}
                                  documentCount={treePersonActivity.doc[p.id] ?? 0}
                                />
                              </SelectItem>
                            ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3 text-sm">
                    <p className="text-muted-foreground">
                      This will make <strong>{allPeople.find(p => p.id === selectedPersonId)?.given_names}</strong> a child of the shared parent, 
                      creating the sibling relationship.
                    </p>
                  </div>
                </div>
              )}

              {connectionType === 'partner' && (
                <>
                  <div>
                    <Label>Type of Partnership</Label>
                    <Select 
                      value={connectionDetails.partnership_type} 
                      onValueChange={(value) => setConnectionDetails({...connectionDetails, partnership_type: value})}
                    >
                      <SelectTrigger data-testid="partnership-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="marriage">Marriage</SelectItem>
                        <SelectItem value="domestic_partnership">Partnership</SelectItem>
                        <SelectItem value="engaged">Engaged</SelectItem>
                        <SelectItem value="divorced">Divorced</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Date (optional)</Label>
                      <Input 
                        type="date" 
                        value={connectionDetails.start_date} 
                        onChange={(e) => setConnectionDetails({...connectionDetails, start_date: e.target.value})}
                        data-testid="partnership-date-input"
                      />
                    </div>
                    <div>
                      <Label>Place (optional)</Label>
                      <Input 
                        placeholder="e.g. parish, town, or county"
                        value={connectionDetails.start_place} 
                        onChange={(e) => setConnectionDetails({...connectionDetails, start_place: e.target.value})}
                        data-testid="partnership-place-input"
                      />
                    </div>
                  </div>
                </>
              )}

              <div>
                <Label>Notes (optional)</Label>
                <Textarea 
                  placeholder="Add any additional information..."
                  value={connectionDetails.notes} 
                  onChange={(e) => setConnectionDetails({...connectionDetails, notes: e.target.value})}
                  rows={3}
                  data-testid="connection-notes-input"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            {connectionStep === 1 && (
              <>
                <Button variant="outline" onClick={() => setIsConnectionDialogOpen(false)}>Cancel</Button>
                <Button onClick={() => setConnectionStep(2)} disabled={!connectionType} data-testid="connection-next-btn">Next</Button>
              </>
            )}
            {connectionStep === 2 && (
              <>
                <Button variant="outline" onClick={() => setConnectionStep(1)}>Back</Button>
                <Button onClick={() => setConnectionStep(3)} disabled={!selectedPersonId} data-testid="connection-next-btn-2">Next</Button>
              </>
            )}
            {connectionStep === 3 && (
              <>
                <Button variant="outline" onClick={() => setConnectionStep(2)}>Back</Button>
                <Button onClick={handleAddConnection} data-testid="connection-finish-btn">{ADD_CONNECTION}</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive Confirmation Dialog */}
      <Dialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <DialogContent className="max-w-md" data-testid="archive-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl flex items-center gap-2">
              <Archive className="h-5 w-5" />
              Archive this profile?
            </DialogTitle>
            <DialogDescription className="text-base pt-2">
              This will remove the profile from active archive views. Existing saved memories are not permanently deleted.
            </DialogDescription>
          </DialogHeader>
          
          <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 my-4">
            <p className="text-sm text-muted-foreground">
              Stories, photos, and connections are preserved. You can restore this profile anytime from your archived people list.
            </p>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowArchiveDialog(false)} className="flex-1">
              Cancel
            </Button>
            <Button onClick={handleArchive} disabled={archiving} className="flex-1" data-testid="confirm-archive-btn">
              {archiving ? 'Archiving...' : 'Archive profile'}
            </Button>
          </DialogFooter>
          
          <div className="border-t pt-4 mt-2">
            <button 
              onClick={handleDeleteCheck}
              className="text-sm text-muted-foreground hover:text-destructive transition-colors underline"
              data-testid="show-delete-btn"
            >
              I need to permanently delete this person instead
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog - Strong Confirmation */}
      <Dialog open={showDeleteDialog} onOpenChange={(open) => {
        setShowDeleteDialog(open);
        if (!open) setDeleteAcknowledged(false);
      }}>
        <DialogContent className="max-w-md" data-testid="delete-dialog">
          <DialogHeader>
            <div className="mx-auto w-14 h-14 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
              <AlertTriangle className="h-7 w-7 text-destructive" />
            </div>
            <DialogTitle className="font-serif text-xl text-center">
              Permanently Delete {person?.given_names}?
            </DialogTitle>
            <DialogDescription className="text-center text-base pt-2">
              This action is <strong>irreversible</strong>. All stories, recordings, and related data will be permanently erased and cannot be recovered.
            </DialogDescription>
          </DialogHeader>
          
          {deleteInfo && (
            <div className="space-y-3 my-4">
              {/* Data that will be lost */}
              <div className="bg-muted/50 rounded-xl p-4 space-y-2">
                <p className="text-sm font-medium text-foreground mb-3">The following will be permanently deleted:</p>
                
                <div className="flex items-center gap-3 text-sm">
                  <div className="w-8 h-8 rounded-lg bg-destructive/10 flex items-center justify-center">
                    <User className="h-4 w-4 text-destructive" />
                  </div>
                  <span>Profile and all personal information</span>
                </div>
                
                {deleteInfo.story_count > 0 && (
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-8 h-8 rounded-lg bg-destructive/10 flex items-center justify-center">
                      <Mic className="h-4 w-4 text-destructive" />
                    </div>
                    <span><strong>{deleteInfo.story_count}</strong> {deleteInfo.story_count === 1 ? 'story' : 'stories'} and voice recordings</span>
                  </div>
                )}
                
                {deleteInfo.document_count > 0 && (
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-8 h-8 rounded-lg bg-destructive/10 flex items-center justify-center">
                      <FileImage className="h-4 w-4 text-destructive" />
                    </div>
                    <span><strong>{deleteInfo.document_count}</strong> {deleteInfo.document_count === 1 ? 'document' : 'documents'} and photos</span>
                  </div>
                )}
                
                {deleteInfo.relationship_count > 0 && (
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                      <Users className="h-4 w-4 text-amber-700" />
                    </div>
                    <span><strong>{deleteInfo.relationship_count}</strong> family connections will be unlinked</span>
                  </div>
                )}
              </div>

              {/* Strong recommendation to archive */}
              {(deleteInfo.story_count > 0 || deleteInfo.document_count > 0) && (
                <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
                  <p className="text-sm">
                    <strong>Consider archiving instead.</strong> Archived people can be restored anytime, preserving their stories for future generations.
                  </p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-3"
                    onClick={() => {
                      setShowDeleteDialog(false);
                      setShowArchiveDialog(true);
                    }}
                  >
                    <Archive className="h-4 w-4 mr-2" />
                    Archive Instead
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Acknowledgment Checkbox */}
          <div className="border-t pt-4">
            <label className="flex items-start gap-3 cursor-pointer" data-testid="delete-acknowledge-label">
              <Checkbox 
                id="delete-acknowledge"
                checked={deleteAcknowledged}
                onCheckedChange={setDeleteAcknowledged}
                className="mt-0.5"
                data-testid="delete-acknowledge-checkbox"
              />
              <span className="text-sm text-muted-foreground">
                I understand that deleting {person?.given_names} is permanent and all their stories, recordings, and related content will be lost forever.
              </span>
            </label>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2 mt-4">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowDeleteDialog(false);
                setDeleteAcknowledged(false);
              }} 
              className="flex-1"
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => handleDelete(true)} 
              disabled={deleting || !deleteAcknowledged}
              className="flex-1"
              data-testid="confirm-delete-btn"
            >
              {deleting ? 'Deleting...' : 'Delete Permanently'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PersonDetail;